import { useMemo } from 'react'
import { ConventionRegistrationProductQuery } from '@/generated/graphql'
import {
  conventionCurrencyFromMeta,
  getConventionBrandCode,
  type ConventionCurrencyDisplay,
} from '@/utils/conventions/getConventionBrandCode'
import { useEcommProductByStubWithUnlock } from './useEcommProductByStubWithUnlock'
import { useStorefrontMarket } from '@/useStorefrontMarket'

type ConventionRegistrationProduct =
  ConventionRegistrationProductQuery['ecommProductBySlug']

type UseConventionProductByStubOptions = {
  initialProduct?: ConventionRegistrationProduct
  initialBrandCode?: ConventionCurrencyDisplay['brandCode']
}

export function useConventionProductByStub(
  stub: string,
  options?: UseConventionProductByStubOptions
) {
  const storefrontMarket = useStorefrontMarket()
  const configBrandCode = getConventionBrandCode(stub)
  const preferredBrandCode =
    options?.initialBrandCode ??
    configBrandCode ??
    storefrontMarket.brandCode ??
    'VANISHING_INC_US'

  const initial = useEcommProductByStubWithUnlock(
    stub,
    preferredBrandCode,
    options?.initialProduct
  )

  const initialCurrency = useMemo(() => {
    const currencyMetaValue = initial.product?.meta?.find(
      m => m.key === 'currency'
    )?.value
    return conventionCurrencyFromMeta(currencyMetaValue)
  }, [initial.product?.meta])

  const forcedBrandCode = initialCurrency?.brandCode ?? null
  const needsBrandRefetch =
    forcedBrandCode != null && forcedBrandCode !== preferredBrandCode

  const forced = useEcommProductByStubWithUnlock(
    needsBrandRefetch ? stub : '',
    forcedBrandCode ?? preferredBrandCode
  )

  const product = needsBrandRefetch ? forced.product : initial.product
  const currency = useMemo(() => {
    if (!needsBrandRefetch) return initialCurrency
    return conventionCurrencyFromMeta(
      forced.product?.meta?.find(m => m.key === 'currency')?.value
    )
  }, [needsBrandRefetch, initialCurrency, forced.product?.meta])

  return {
    product,
    currency,
    brandCode:
      (needsBrandRefetch ? forcedBrandCode : preferredBrandCode) ??
      preferredBrandCode,
    isPending:
      initial.isPending ||
      (needsBrandRefetch &&
        (forced.isPending || (!forced.product && !forced.isError))),
    isError: needsBrandRefetch ? forced.isError : initial.isError,
  }
}
